import type {
  EffectiveTrackingConsentPolicy,
  StoredTrackingConsentDecision,
  TrackingConsentDecision,
  TrackingConsentScope
} from './types';

const COOKIE_PREFIX = 'gc_tc_';
const SECONDS_PER_DAY = 24 * 60 * 60;
const MAX_CONSENT_COOKIES = 50;

type StoredConsent = {
  decision: StoredTrackingConsentDecision;
  policy_version: string;
  decided_at: string;
};

type ParsedConsent = StoredConsent & {
  decidedAt: Date;
};

type CookieEntry = {
  name: string;
  value: string;
};

type CookieLocation = Pick<Location, 'hostname' | 'protocol'>;

type ReadTrackingConsentOptions = {
  document: Document;
  scope: TrackingConsentScope;
  policyVersion: string;
  preferenceLifetimeDays: number;
  now?: () => Date;
};

type WriteTrackingConsentOptions = ReadTrackingConsentOptions & {
  location: CookieLocation;
  decision: StoredTrackingConsentDecision;
};

const scopePrefixes: Record<TrackingConsentScope['type'], string> = {
  event: 'e',
  series: 's',
  hub: 'h'
};

const encodeConsent = (consent: StoredConsent): string =>
  btoa(JSON.stringify(consent)).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');

const decodeConsent = (value: string): ParsedConsent | null => {
  try {
    const base64 = value.replaceAll('-', '+').replaceAll('_', '/');
    const paddedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const parsed = JSON.parse(atob(paddedBase64)) as unknown;

    if (!isStoredConsent(parsed)) {
      return null;
    }

    const decidedAt = new Date(parsed.decided_at);
    if (Number.isNaN(decidedAt.getTime())) {
      return null;
    }

    return { ...parsed, decidedAt };
  } catch {
    return null;
  }
};

const isStoredConsent = (value: unknown): value is StoredConsent => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<StoredConsent>;
  return (
    (candidate.decision === 'allowed' || candidate.decision === 'rejected') &&
    typeof candidate.policy_version === 'string' &&
    candidate.policy_version.length > 0 &&
    typeof candidate.decided_at === 'string'
  );
};

const parseCookies = (cookieHeader: string): CookieEntry[] => {
  const cookies: CookieEntry[] = [];

  for (const part of cookieHeader.split(';')) {
    const separator = part.indexOf('=');
    if (separator < 0) {
      continue;
    }

    const name = part.slice(0, separator).trim();
    if (!name) {
      continue;
    }

    cookies.push({ name, value: part.slice(separator + 1).trim() });
  }

  return cookies;
};

const cookieValues = (cookies: CookieEntry[], name: string): string[] =>
  cookies.filter(cookie => cookie.name === name).map(cookie => cookie.value);

const isExpired = (decidedAt: Date, now: Date, preferenceLifetimeDays: number): boolean => {
  const age = now.getTime() - decidedAt.getTime();
  return age < 0 || age >= preferenceLifetimeDays * SECONDS_PER_DAY * 1000;
};

const isLocalHttp = (location: CookieLocation): boolean =>
  location.protocol === 'http:' &&
  (location.hostname === 'localhost' || location.hostname === '127.0.0.1' || location.hostname === '::1');

const cookieAttributes = (location: CookieLocation, maxAge: number): string => {
  const attributes = [`Max-Age=${maxAge}`, 'Path=/', 'SameSite=Lax'];

  if (!isLocalHttp(location)) {
    attributes.push('Secure');
  }

  return attributes.join('; ');
};

const deleteCookie = (document: Document, location: CookieLocation, name: string): void => {
  document.cookie = `${name}=; ${cookieAttributes(location, 0)}`;
};

export const buildTrackingConsentCookieName = (scope: TrackingConsentScope): string => {
  const resourceId = scope.id.replaceAll('-', '');
  if (!resourceId || !/^[a-zA-Z0-9_]+$/u.test(resourceId)) {
    throw new Error('Tracking consent scope ID must be alphanumeric');
  }

  return `${COOKIE_PREFIX}${scopePrefixes[scope.type]}_${resourceId}`;
};

export const readTrackingConsentDecision = ({
  document,
  scope,
  policyVersion,
  preferenceLifetimeDays,
  now = () => new Date()
}: ReadTrackingConsentOptions): TrackingConsentDecision => {
  if (!Number.isFinite(preferenceLifetimeDays) || preferenceLifetimeDays <= 0) {
    return 'unknown';
  }

  try {
    const name = buildTrackingConsentCookieName(scope);
    const values = cookieValues(parseCookies(document.cookie), name);
    if (values.length !== 1) {
      return 'unknown';
    }

    const consent = decodeConsent(values[0]);
    if (
      !consent ||
      consent.policy_version !== policyVersion ||
      isExpired(consent.decidedAt, now(), preferenceLifetimeDays)
    ) {
      return 'unknown';
    }

    return consent.decision;
  } catch {
    return 'unknown';
  }
};

export const writeTrackingConsentDecision = ({
  document,
  location,
  scope,
  decision,
  policyVersion,
  preferenceLifetimeDays,
  now = () => new Date()
}: WriteTrackingConsentOptions): boolean => {
  if (!Number.isFinite(preferenceLifetimeDays) || preferenceLifetimeDays <= 0) {
    return false;
  }

  try {
    const decisionTime = now();
    const name = buildTrackingConsentCookieName(scope);
    const value = encodeConsent({
      decision,
      policy_version: policyVersion,
      decided_at: decisionTime.toISOString()
    });
    const maxAge = Math.floor(preferenceLifetimeDays * SECONDS_PER_DAY);

    document.cookie = `${name}=${value}; ${cookieAttributes(location, maxAge)}`;

    const writtenCookies = parseCookies(document.cookie);
    const writtenValues = cookieValues(writtenCookies, name);
    if (writtenValues.length !== 1 || writtenValues[0] !== value) {
      return false;
    }

    const consentCookies = writtenCookies
      .filter(cookie => cookie.name.startsWith(COOKIE_PREFIX))
      .map(cookie => ({
        name: cookie.name,
        consent: decodeConsent(cookie.value)
      }));

    const retainedCookies: Array<{ name: string; decidedAt: Date }> = [];
    for (const cookie of consentCookies) {
      if (!cookie.consent || cookie.consent.decidedAt.getTime() > decisionTime.getTime()) {
        deleteCookie(document, location, cookie.name);
        continue;
      }

      retainedCookies.push({
        name: cookie.name,
        decidedAt: cookie.consent.decidedAt
      });
    }

    retainedCookies.sort((left, right) => {
      const timeDifference = right.decidedAt.getTime() - left.decidedAt.getTime();
      if (timeDifference !== 0) {
        return timeDifference;
      }

      // tiebreaker: prioritise the newly-written cookie when two share the same millisecond timestamp
      if (left.name === name) {
        return -1;
      }
      if (right.name === name) {
        return 1;
      }
      return left.name.localeCompare(right.name);
    });

    const cookieNamesToDelete = new Set(retainedCookies.slice(MAX_CONSENT_COOKIES).map(cookie => cookie.name));
    for (const cookieName of cookieNamesToDelete) {
      deleteCookie(document, location, cookieName);
    }

    const finalValues = cookieValues(parseCookies(document.cookie), name);
    return finalValues.length === 1 && finalValues[0] === value;
  } catch {
    return false;
  }
};

/**
 * Reads a consent decision from a raw HTTP Cookie header string. Safe to call
 * in Node.js SSR — no DOM or browser APIs required.
 */
export const readTrackingConsentDecisionFromHeader = (
  cookieHeader: string | undefined,
  scope: TrackingConsentScope,
  policyVersion: string,
  preferenceLifetimeDays: number,
  now: () => Date = () => new Date()
): TrackingConsentDecision => {
  if (!cookieHeader || !Number.isFinite(preferenceLifetimeDays) || preferenceLifetimeDays <= 0) {
    return 'unknown';
  }

  try {
    const name = buildTrackingConsentCookieName(scope);
    const values = cookieValues(parseCookies(cookieHeader), name);
    if (values.length !== 1) {
      return 'unknown';
    }

    const consent = decodeConsent(values[0]);
    if (
      !consent ||
      consent.policy_version !== policyVersion ||
      isExpired(consent.decidedAt, now(), preferenceLifetimeDays)
    ) {
      return 'unknown';
    }

    return consent.decision;
  } catch {
    return 'unknown';
  }
};

/**
 * Whether non-essential tracking may be emitted for this SSR request.
 * Legacy/disabled policies fall open to preserve existing behavior.
 */
export const canEmitTrackingScripts = (
  cookieHeader: string | undefined,
  policy:
    | Pick<EffectiveTrackingConsentPolicy, 'enabled' | 'policy_version' | 'preference_lifetime_days'>
    | null
    | undefined,
  scope: TrackingConsentScope,
  now: () => Date = () => new Date()
): boolean => {
  if (!policy || policy.enabled !== true) {
    return true;
  }
  return (
    readTrackingConsentDecisionFromHeader(
      cookieHeader,
      scope,
      policy.policy_version,
      policy.preference_lifetime_days,
      now
    ) === 'allowed'
  );
};
